import { createApi } from "@reduxjs/toolkit/query/react"
import axiosBaseQuery from "../../../axiosBaseQuery"
import { authService } from "../../../../services/authService"

/**
 * Auth API на RTK Query (EyeHub, SSO-only).
 *
 * Дизайн: единственный fetch на init через initializeAuth
 * (authService.initialize() → GET /api/v1/auth/status/). Данные user/auth_status
 * кешируются в Redux. login — SSO-redirect (кнопка на Login.js), logout — через
 * unified endpoint.
 */
export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["Auth", "User"],
  endpoints: (builder) => ({
    // SSO-логин: редирект на Keycloak. Компонент Login.js вызывает эту мутацию,
    // authService.login() делает window.location = /api/v1/auth/keycloak/login/.
    login: builder.mutation({
      queryFn: async () => {
        try {
          const result = await authService.login();
          return { data: result };
        } catch (error) {
          return { error: { status: "LOGIN_ERROR", data: error.message } };
        }
      },
      invalidatesTags: ["Auth", "User"],
    }),

    // Выход из системы.
    logout: builder.mutation({
      queryFn: async (_arg, { dispatch }) => {
        try {
          await authService.logout();
          dispatch(authApi.util.resetApiState());
          return { data: { success: true } };
        } catch (error) {
          dispatch(authApi.util.resetApiState());
          return { error: { status: "LOGOUT_ERROR", data: error.message } };
        }
      },
      invalidatesTags: ["Auth", "User"],
    }),

    // Инициализация аутентификации — единственный status fetch на startup.
    initializeAuth: builder.mutation({
      queryFn: async () => {
        try {
          const status = await authService.initialize();
          return {
            data: {
              initialized: true,
              authenticated: status?.authenticated ?? false,
              user: status?.user ?? null,
            },
          };
        } catch (error) {
          return {
            data: { initialized: true, authenticated: false, user: null },
          };
        }
      },
      invalidatesTags: ["Auth", "User"],
    }),
  }),
});

export const {
  useLoginMutation,
  useLogoutMutation,
  useInitializeAuthMutation,
} = authApi;
